import { action } from 'typesafe-actions';
import store from 'store';

/** Dispatch of actions promisable:
 * Resources for handling the dispatch of asynchronous actions using redux sagas.
 * Provides a promise for the dispatched actions, so it is possible
 * to wait for the result of these actions.
 *
 * Necessary adjustments in the Actions file:
 *  * Import function asyncAction and use it as an action creator.
 *    * export const actionRequest =
 *          (payload: object) => asyncAction(ActionTypes.ACTION_REQUEST, payload);
 *
 * Necessary adjustments in the Sagas file:
 *  * Add the meta argument to the generator function.
 *      * function* generator ({ payload, meta }: ...) { ... }
 *  * Finally, add instructions below:
 *      * meta.promise.resolve(response); -> Success (status === 200);
 *      * meta.promise.reject(error); -> Failure (catch).
 *
 * Necessary adjustments in the dispatch of the actions:
 *  * Import the promiseDispatch function and use it as a dispatcher function.
 *     * const result = await promiseDispatch( action(payload) );
 */

/**
 * Creates the promise that will be returned in the dispatch of the action.
 */
const createExposedPromise = () => {
  const deferred: any = { resolve: null, reject: null };

  const promise = new Promise((resolve, reject) => {
    deferred.resolve = resolve;
    deferred.reject = reject;
  });

  return [promise, deferred];
};

// Middleware
export const promiseMiddleware = (store: any) => (next: any) => (
  action: any,
) => {
  if (!(action.meta && action.meta.promise)) {
    return next(action);
  }

  const [promise, deferred] = createExposedPromise();
  next({ ...action, meta: { promise: deferred } });
  return promise;
};

// Action Template
export const asyncAction = (type: string, payload: any) =>
  action(type, payload, {
    promise: (true as unknown) as {
      resolve: (value?: unknown) => void;
      reject: (reason?: any) => void;
    },
  });

// Promise Dispatch
export const promiseDispatch = (action: any) =>
  store.dispatch(action) as Promise<any>;
